import { useRecoilState } from 'recoil';
import { CheckBoxField } from 'components/core';
import { builderAnswersState } from 'states';
import { cumulativeAllowed, source } from 'helpers/kpiBuilder/lookups';
import { toggleInList } from 'helpers/kpiBuilder/transitions';
import { Interval, QuestionKind, RecipeId } from 'helpers/kpiBuilder/types';
import { PROGRESS_KINDS } from 'helpers/kpiBuilder/progressRecipes';
import { BarNamesStep } from './BarNamesStep';
import { BucketsStep } from './BucketsStep';
import { ReadyOptions } from './ReadyOptions';
import { TopN } from './TopN';
import { ExtraSeriesStep } from './ExtraSeriesStep';
import { BuilderSection } from './BuilderSection';
import { GroupStep } from './GroupStep';
import { MeasureStep } from './MeasureStep';
import { RangesStep } from './RangesStep';
import { StepProps } from './StepProps';
import { TraitStep } from './TraitStep';
import { TypedColumns } from './TypedColumns';
import { OptionCards } from './OptionCards';
import { OptionChips } from './OptionChips';

const INTERVALS = (['day', 'week', 'month'] as Interval[]).map((i) => ({ id: i, label: i.charAt(0).toUpperCase() + i.slice(1) }));
const ROWS = [100, 1000, 5000].map((n) => ({ id: n, label: String(n) }));

const MIN_COUNTS = [1, 5, 10, 25, 50].map((n) => ({ id: n, label: n === 1 ? 'Show all' : `At least ${n}` }));

const MinCount = ({ a, patch }: StepProps) => (
  <BuilderSection title="Ignore groups with few records" hint="Skip groups that have too few records to mean much, so one 5-star rating cannot outrank 200 ratings averaging 4.8.">
    <OptionChips options={MIN_COUNTS} selected={[a.minCount]} onSelect={(id) => patch({ minCount: Number(id) })} />
  </BuilderSection>
);

const Share = (p: StepProps) => (
  <>
    <GroupStep {...p} />
    <BucketsStep {...p} />
    <TopN {...p} />
  </>
);

const Trend = (p: StepProps) => (
  <>
    <MeasureStep {...p} />
    <BuilderSection title="One point per" hint="How often to add a point. Days show detail but look busy over long periods; months show the bigger picture.">
      <OptionChips options={INTERVALS} selected={[p.a.interval]} onSelect={(id) => p.patch({ interval: id as Interval })} />
    </BuilderSection>
    <BuilderSection title="Running total" hint={cumulativeAllowed(p.a) ? 'Off: each point is that period only. On: each point adds up everything so far, so the line only goes up.' : 'A running total does not make sense for an average, so it is switched off.'}>
      {cumulativeAllowed(p.a) && <CheckBoxField label="Add up as time goes on" isChecked={p.a.cumulative} clickHandler={() => p.patch({ cumulative: !p.a.cumulative })} />}
    </BuilderSection>
  </>
);

const Rate = (p: StepProps) => (
  <>
    <GroupStep {...p} />
    <TraitStep {...p} title="Percentage of learners who…" hint="For each group: learners who match this, divided by all counted learners in that group. Pick the trait you want the percentage of." />
    <TopN {...p} />
  </>
);

const Compare = (p: StepProps) => (
  <>
    <MeasureStep {...p} />
    <GroupStep {...p} />
    <TraitStep {...p} title="Compare against" hint="Two bars per group: the first counts everyone you chose above, the second counts only the learners in this group. Pick the trait to compare, such as “Passed”." />
    <BarNamesStep {...p} />
    <ExtraSeriesStep {...p} />
    <TopN {...p} />
  </>
);

const List = ({ a, s, patch }: StepProps) => (
  <>
    <BuilderSection title="Columns" hint="One column per piece of information. Without a Name column people only see IDs. Phone, email and registration numbers can never be added here.">
      <OptionChips
        options={s.cols.map((c) => ({ id: c.id, label: c.pii ? `${c.label} (personal data, locked)` : c.label, disabled: c.pii }))}
        selected={a.cols}
        onSelect={(id) => patch({ cols: toggleInList(a.cols, String(id)) })}
      />
    </BuilderSection>
    <BuilderSection title="Maximum rows" hint="The most learners the table will load. Bigger lists load slower.">
      <OptionChips options={ROWS} selected={[a.rows]} onSelect={(id) => patch({ rows: Number(id) })} />
    </BuilderSection>
    <TypedColumns a={a} s={s} patch={patch} />
  </>
);

const PerGroup = (p: StepProps) => (
  <>
    <MeasureStep {...p} />
    <GroupStep {...p} />
    <TopN {...p} />
    <MinCount {...p} />
  </>
);

const QUESTION_KINDS = [
  { id: 'difficulty', label: 'Which questions are hardest?', desc: 'Answers: which questions learners get wrong most. You get: one bar per question showing the % of answers that were correct, hardest first.' },
  { id: 'cohort', label: 'Do the questions separate strong and weak learners?', desc: 'Answers: whether a question tells strong learners from weak ones. You get: a heat map of first-attempt answers, split by whether the learner went on to pass.' },
];

const Questions = (p: StepProps) => (
  <BuilderSection title="What do you want to check?" hint="Both look at the same answers. Pick the learners above to narrow them, for example one cadre.">
    <OptionCards cards={QUESTION_KINDS} selected={[p.a.questionKind]} onSelect={(id) => p.patch({ questionKind: id as QuestionKind })} />
  </BuilderSection>
);

const Gap = () => <div className="small text-secondary">A list of these learners is created automatically so the number can be clicked.</div>;

const COURSE_BARS = [{ id: 'competency', label: 'Competency (named by competency)' }, { id: 'course', label: 'Course' }];

// The bar names come from the dashboard, so they are shown here rather than chosen.
const BarNames = ({ a }: StepProps) => {
  const bars = PROGRESS_KINDS[a.progressKind].bars;
  return (
    <BuilderSection title="Bars the dashboard will show" hint="The dashboard names and colours these bars itself, so they cannot be renamed here. To use your own names, build the KPI with “Compare two numbers per group” instead.">
      {bars.length ? <ul className="mb-0">{bars.map((b) => <li key={b}>{b}</li>)}</ul> : <div className="small text-secondary">Two bars per district, named by the dashboard.</div>}
    </BuilderSection>
  );
};

const Progress = (p: StepProps) => (
  <>
    {p.a.progressKind === 'course' && (
      <BuilderSection title="One bar for each" hint="Bars can be named by competency, or by each course.">
        <OptionChips options={COURSE_BARS} selected={[p.a.group ?? 'competency']} onSelect={(id) => p.patch({ group: String(id) })} />
      </BuilderSection>
    )}
    <BarNames {...p} />
  </>
);

const STEPS: Record<Exclude<RecipeId, 'funnel' | 'template' | 'advanced'>, (p: StepProps) => JSX.Element> = {
  count: MeasureStep, perGroup: PerGroup, trend: Trend, rate: Rate, gap: Gap, list: List, compare: Compare, share: Share, questions: Questions, ranges: RangesStep, progress: Progress, ready: ReadyOptions,
};

// A funnel, a copied KPI and a KPI built part by part have their own screens instead.
const hasStep = (r: RecipeId): r is keyof typeof STEPS => r in STEPS;

// Step 4: the controls that depend on the question.
export const DetailsStep = () => {
  const [a, setA] = useRecoilState(builderAnswersState);
  const s = source(a);
  if (!s || !a.recipe || !hasStep(a.recipe)) return null;
  const Step = STEPS[a.recipe];

  return (
    <div>
      <Step a={a} s={s} patch={(p) => setA({ ...a, ...p })} />
    </div>
  );
};
